import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { SessionView } from '../api/types';
import { REMINDER_LEAD_MINUTES, scheduleLocalReminder, useReminderPermission } from '../lib/reminders';
import { useCountdown } from '../lib/use-countdown';
import { Badge, Card, Muted, SecondaryButton } from './ui';

/**
 * The reminder block shown wherever the old UI promised "we'll remind you".
 *
 * The state is Notification.permission — the only real switch that exists
 * (no web push subscription exists end-to-end, see lib/reminders.ts). Each
 * state says exactly what will happen and nothing more:
 *  - granted: a notification fires on this device 15 min out, page-alive only;
 *  - default: off, with the one action that can actually turn it on;
 *  - denied:  off, and only the browser's site settings can change that;
 *  - unsupported: no notification machinery at all — the countdown is the plan.
 */
export function ReminderStatus({ session }: { session: SessionView }) {
  const { t } = useTranslation();
  const { permission, request } = useReminderPermission();
  const remaining = useCountdown(session.expiresAt, session.remainingSeconds);
  const active = session.status === 'active' && remaining > 0;
  const lotName = session.lot.name;

  useEffect(() => {
    if (permission !== 'granted' || !active) return;
    scheduleLocalReminder(
      session.id,
      session.expiresAt,
      t('reminder.notifTitle'),
      t('reminder.notifBody', { plate: session.plate, lot: lotName }),
    );
    // `remaining` is deliberately absent: the timer re-arms on expiry changes
    // (extensions), not on every countdown tick.
  }, [permission, active, session.id, session.expiresAt, session.plate, lotName, t]);

  if (!active) return null;

  const withinLead = remaining <= REMINDER_LEAD_MINUTES * 60;
  const on = permission === 'granted';

  return (
    <Card>
      <div className="flex items-center justify-between gap-3">
        <span className="text-[15px] font-semibold">{t('reminder.label')}</span>
        {permission === 'unsupported' ? null : (
          <Badge tone={on ? 'success' : 'warning'}>{t(on ? 'reminder.on' : 'reminder.off')}</Badge>
        )}
      </div>
      <Muted>
        {permission === 'unsupported'
          ? t('reminder.unsupported')
          : permission === 'denied'
            ? t('reminder.denied')
            : on
              ? t(withinLead ? 'reminder.soonBody' : 'reminder.onBody')
              : t('reminder.offBody')}
      </Muted>
      {permission === 'default' ? (
        <SecondaryButton label={t('reminder.enable')} onClick={() => void request()} />
      ) : null}
    </Card>
  );
}
